import React, { useState } from "react";
import { Box, Divider } from "@mui/material";
import {
  add_expense_category,
  update_expense_category,
} from "../../features/expense/expenseCategoryService";
import { StyledDialogTitle } from "../common/StyledDialogTitle";
import AppButton from "../common/AppButton";
import CommonInputField from "../common/InputField/CommonInputField";

interface IProps {
  handleClose: () => void;
  initialData?: any;
  isEdit?: boolean;
  setReloadCategory: any;
  onUpdateSuccess?: () => void;
  onAddSuccess?: () => void;
  onUpdateError?: (message: string) => void; 
  onAddError?: (message: string) => void;
}

const AddNewCategoryModal = ({
  handleClose,
  isEdit,
  initialData,
  setReloadCategory,
  onUpdateSuccess,
  onAddSuccess,
  onUpdateError,
  onAddError
}: IProps) => {
  const [categoryForm, setCategoryForm] = useState({
    categoryname: isEdit ? initialData?.name : "",
    description: isEdit ? initialData?.description : "",
  });

  const [formErrors, setFormErrors] = useState({
    categoryname: "",
    description: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field: string, value: string) => {
    setCategoryForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error as user types
    setFormErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validateForm = () => {
    const errors: any = {};
    if (!categoryForm.categoryname.trim()) {
      errors.categoryname = "Category Name is required";
    }
    if (!categoryForm.description.trim()) {
      errors.description = "Description is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const addCategory = async () => {
    const dto = {
      name: categoryForm.categoryname,
      description: categoryForm.description,
    };
    try {
      setIsLoading(true);
      const res = await add_expense_category(dto);
      if (res) {
        // alert("Category added successfully");
        setReloadCategory?.((prev: boolean) => !prev);
        setIsLoading(false);
        handleClose();
        onAddSuccess?.();
      }
    } catch (error: any) {
      if (onAddError) {
        onAddError(error?.response?.data?.message || "Category Name Already Exists");
      }
      setIsLoading(false);
    }
  };

  const updateCategory = async () => {
    const dto = {
      name: categoryForm.categoryname,
      description: categoryForm.description,
      exp_category_id: initialData?.id,
    };
    try {
      setIsLoading(true);
      const res = await update_expense_category(dto);
      if (res) {
        setReloadCategory?.((prev: boolean) => !prev);
        setIsLoading(false);
        handleClose();
        onUpdateSuccess?.();
      }
    } catch (error: any) {
      if (onUpdateError) {
        onUpdateError(error?.response?.data?.message || "Update failed");
      }
      setIsLoading(false);
      console.error(error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (isEdit) {
      updateCategory();
    } else {
      addCategory();
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        bgcolor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          maxWidth: "460px",
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: 24,
          position: "relative",
        }}
      >
        <StyledDialogTitle
          id="customized-dialog-title"
          onClose={handleClose}
          isDone={false}
        >
          {isEdit ? "Update Category" : "Add Category"}
        </StyledDialogTitle>

        <Divider />
        <Box sx={{ p: 2 }}>
          <Box sx={{ px: 2 }}>
            <CommonInputField
              label="Category Name"
              required
              placeholder="Office Supplies"
              value={categoryForm.categoryname}
              onChange={(e) => handleChange("categoryname", e.target.value)}
              error={!!formErrors.categoryname}
              helperText={formErrors.categoryname}
            />
          </Box>

          <Box sx={{ px: 2, mt: 3 }}>
            <CommonInputField
              label="Description"
              required
              placeholder="Enter Description..."
              value={categoryForm.description}
              onChange={(e) => handleChange("description", e.target.value)}
              multiline
              rows={3}
              error={!!formErrors.description}
              helperText={formErrors.description}
            />
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 2 }}
          >
            <AppButton
              type="submit"
              title={isEdit ? "Update" : "Add"}
              isLoading={isLoading}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddNewCategoryModal;
